import React from 'react';
const { useState, useMemo } = React;
import { CRM_COLUMNS, CRM_RESULT_VALUES, STALLED_DAYS, t } from '../../diagnosticConfig.js';
import { computeCrmMetrics, guessMapping, guessResult, guessSellerLinks, distinctValues, isClosingStage, suggestStageOrder, stageOrderWarnings } from '../../diagnosticCrm.js';
import { Field, inputSt, cardSt, labelSt, mutedSt, warnSt, tableSt, thSt, tdSt, fmtInt, fmtPct, fmtNum, fmtDate } from './shared.jsx';

// CRM export: the file (CSV or Excel) is read in the browser and never uploaded
// or stored. Ale maps the columns and links each CRM seller to a participant;
// the metrics are computed here in code, and only they (plus the name links) are
// saved in Diagnostics › CRM Metrics.

const today = () => new Date().toISOString().slice(0, 10);
const MAX_ROWS = 50_000;

async function readFile(file, sheetName) {
  if (/\.csv$|\.txt$/i.test(file.name)) {
    const Papa = (await import('papaparse')).default;
    return new Promise((resolve, reject) => Papa.parse(file, {
      header: true, skipEmptyLines: 'greedy',
      complete: (r) => resolve({ rows: r.data, headers: (r.meta.fields || []).filter(Boolean), sheets: [] }),
      error: reject,
    }));
  }
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
  const name = sheetName && wb.SheetNames.includes(sheetName) ? sheetName : wb.SheetNames[0];
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { defval: '', raw: true });
  return { rows, headers: Object.keys(rows[0] || {}).filter(h => !/^__EMPTY/.test(h)), sheets: wb.SheetNames, sheet: name };
}

export default function CrmImport({ diagnostic, participants, busy, onSave }) {
  const saved = diagnostic.crmMetrics;
  const [file, setFile] = useState(null);
  const [data, setData] = useState(null); // { rows, headers, sheets, sheet } — memory only
  const [readError, setReadError] = useState('');
  const [mapping, setMapping] = useState({ qualification: [] });
  const [opts, setOpts] = useState({ dateFormat: 'dmy', decimal: ',', exportDate: today() });
  const [resultMap, setResultMap] = useState({});
  const [stages, setStages] = useState([]); // [{ value, included }]
  const [links, setLinks] = useState({});

  const reset = () => { setFile(null); setData(null); setStages([]); setResultMap({}); setLinks({}); setMapping({ qualification: [] }); };

  const load = async (f, sheet) => {
    setReadError('');
    try {
      const d = await readFile(f, sheet);
      if (!d.rows.length || !d.headers.length) throw new Error('empty');
      if (d.rows.length > MAX_ROWS) throw new Error('too many');
      setFile(f); setData(d);
      const m = guessMapping(d.headers);
      applyMapping(m, d.rows);
    } catch (e) {
      setData(null);
      setReadError(e.message === 'too many' ? `El archivo tiene más de ${fmtInt(MAX_ROWS)} filas.` : 'No se pudo leer el archivo. Revisa que sea un CSV o Excel con una fila de encabezados.');
    }
  };

  // Recompute the value lists that depend on the mapped columns
  const applyMapping = (m, rows = data.rows) => {
    setMapping(m);
    if (m.result) {
      const values = distinctValues(rows, m.result);
      setResultMap(prev => Object.fromEntries(values.map(v => [v, prev[v] || guessResult(v)])));
    } else setResultMap({});
    if (m.stage) {
      const values = distinctValues(rows, m.stage);
      setStages(prev => {
        const kept = prev.filter(s => values.includes(s.value));
        // New stages: a logical order (final stages last), not frequency; final stages left out by default
        const added = suggestStageOrder(values.filter(v => !kept.some(s => s.value === v))).map(v => ({ value: v, included: !isClosingStage(v) }));
        return kept.length ? [...kept, ...added] : added;
      });
    } else setStages([]);
    if (m.seller) {
      const names = distinctValues(rows, m.seller);
      const guessed = guessSellerLinks(names, participants);
      setLinks(prev => Object.fromEntries(names.map(n => [n, n in prev ? prev[n] : (saved?.sellerLinks?.[n] !== undefined ? saved.sellerLinks[n] : guessed[n])])));
    } else setLinks({});
  };

  const requiredMissing = CRM_COLUMNS.filter(c => c.required && !mapping[c.key]);
  const stageOrder = stages.filter(s => s.included).map(s => s.value);
  const misplaced = stageOrderWarnings(stageOrder);

  const metrics = useMemo(() => {
    if (!data || requiredMissing.length) return null;
    return computeCrmMetrics(data.rows, mapping, { ...opts, resultMap, stageOrder, sellerLinks: links });
  }, [data, mapping, opts, resultMap, stages, links]);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= stages.length) return;
    const next = [...stages];
    [next[i], next[j]] = [next[j], next[i]];
    setStages(next);
  };

  const save = async () => {
    if (misplaced.length && !window.confirm(`La etapa final "${misplaced.join('", "')}" está antes que otras etapas del embudo, y eso distorsiona la conversión por etapa. ¿Guardar igual?`)) return;
    if (await onSave(metrics)) reset();
  };

  return (
    <div>
      {saved && !data && (
        <div style={cardSt}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={labelSt}>Métricas guardadas</div>
            <div style={mutedSt}>Export del {fmtDate(saved.exportDate)} · calculadas el {fmtDate(saved.computedAt, true)} · {fmtInt(saved.rows?.used)} oportunidades</div>
          </div>
          <MetricsView metrics={saved} participants={participants} />
          <div style={{ textAlign: 'right', marginTop: 10 }}>
            <button className="action-btn" style={{ fontSize: 12, color: '#B4362C' }} disabled={busy}
              onClick={() => window.confirm('¿Borrar las métricas guardadas del CRM?') && onSave(null)}>🗑 Borrar métricas</button>
          </div>
        </div>
      )}

      <div style={cardSt}>
        <div style={{ ...labelSt, marginBottom: 4 }}>{saved ? 'Reemplazar con otro export' : 'Cargar el export del CRM'}</div>
        <div style={{ ...mutedSt, marginBottom: 10 }}>
          El archivo se lee en este navegador y nunca se sube ni se guarda. Solo se guardan las métricas calculadas y el vínculo de nombres.
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept=".csv,.txt,.xlsx,.xls" onChange={e => e.target.files[0] && load(e.target.files[0])} />
          {data?.sheets?.length > 1 && (
            <select style={{ ...inputSt, width: 200 }} value={data.sheet} onChange={e => load(file, e.target.value)}>
              {data.sheets.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {data && <span style={mutedSt}>{file.name} · {fmtInt(data.rows.length)} filas</span>}
          {data && <button className="action-btn" style={{ fontSize: 12 }} onClick={reset}>Descartar</button>}
        </div>
        {readError && <div style={{ color: '#B4362C', fontSize: 12, marginTop: 8 }}>{readError}</div>}
      </div>

      {data && (
        <>
          <div style={cardSt}>
            <div style={{ ...labelSt, marginBottom: 10 }}>1. Columnas</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
              {CRM_COLUMNS.map(c => (
                <Field key={c.key} label={`${t(c.label)}${c.required ? ' *' : ''}`}>
                  <select style={{ ...inputSt, ...(c.required && !mapping[c.key] ? { borderColor: '#B4362C' } : {}) }} value={mapping[c.key] || ''}
                    onChange={e => applyMapping({ ...mapping, [c.key]: e.target.value || undefined })}>
                    <option value="">{c.required ? 'Elegir…' : 'No está en el archivo'}</option>
                    {data.headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </Field>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <span style={labelSt}>Campos de calificación (opcional)</span>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12 }}>
                {data.headers.map(h => (
                  <label key={h} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input type="checkbox" checked={mapping.qualification.includes(h)}
                      onChange={() => setMapping({ ...mapping, qualification: mapping.qualification.includes(h) ? mapping.qualification.filter(x => x !== h) : [...mapping.qualification, h] })} /> {h}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginTop: 12 }}>
              <Field label="Formato de fechas">
                <select style={inputSt} value={opts.dateFormat} onChange={e => setOpts({ ...opts, dateFormat: e.target.value })}>
                  <option value="dmy">Día/mes/año</option><option value="mdy">Mes/día/año</option>
                </select>
              </Field>
              <Field label="Separador decimal de los montos">
                <select style={inputSt} value={opts.decimal} onChange={e => setOpts({ ...opts, decimal: e.target.value })}>
                  <option value=",">Coma (1.234,56)</option><option value=".">Punto (1,234.56)</option>
                </select>
              </Field>
              <Field label="Fecha del export" hint={`Referencia para "estancadas" (más de ${STALLED_DAYS} días sin cambios).`}>
                <input type="date" style={inputSt} value={opts.exportDate} onChange={e => setOpts({ ...opts, exportDate: e.target.value || today() })} />
              </Field>
            </div>
          </div>

          {requiredMissing.length > 0 && <div style={warnSt}>Faltan columnas obligatorias: {requiredMissing.map(c => t(c.label)).join(', ')}.</div>}

          {mapping.result && (
            <div style={cardSt}>
              <div style={{ ...labelSt, marginBottom: 10 }}>2. Resultado: qué significa cada valor</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8 }}>
                {Object.entries(resultMap).map(([v, r]) => (
                  <div key={v} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</span>
                    <select style={{ ...inputSt, width: 120 }} value={r} onChange={e => setResultMap({ ...resultMap, [v]: e.target.value })}>
                      {CRM_RESULT_VALUES.map(o => <option key={o.key} value={o.key}>{t(o.label)}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stages.length > 0 && (
            <div style={cardSt}>
              <div style={{ ...labelSt, marginBottom: 4 }}>3. Etapas del embudo, en orden</div>
              <div style={{ ...mutedSt, marginBottom: 10 }}>Ordena de la primera a la última y desmarca las etapas de cierre (ganada, perdida).</div>
              {stages.map((s, i) => (
                <div key={s.value} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, opacity: s.included ? 1 : 0.5 }}>
                  <button className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => move(i, -1)}>↑</button>
                  <button className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => move(i, 1)}>↓</button>
                  <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="checkbox" checked={s.included} onChange={() => setStages(stages.map((x, j) => (j === i ? { ...x, included: !x.included } : x)))} />
                    {s.value}
                  </label>
                  {s.included && misplaced.includes(s.value) && <span style={{ fontSize: 11, color: '#9A6212' }}>⚠️ etapa final antes que otras</span>}
                </div>
              ))}
              {misplaced.length > 0 && (
                <div style={{ ...warnSt, fontSize: 12, marginTop: 8, marginBottom: 0 }}>
                  ⚠️ La etapa final {misplaced.map(v => `"${v}"`).join(', ')} está antes que otras etapas del embudo. Muévela al final (o quítala: las ganadas y perdidas ya salen de la columna Resultado).
                </div>
              )}
            </div>
          )}

          {Object.keys(links).length > 0 && (
            <div style={cardSt}>
              <div style={{ ...labelSt, marginBottom: 10 }}>4. Vendedores del CRM ↔ participantes</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 8 }}>
                {Object.entries(links).map(([name, pid]) => (
                  <div key={name} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, flex: 1 }}>{name}</span>
                    <select style={{ ...inputSt, width: 170 }} value={pid || ''} onChange={e => setLinks({ ...links, [name]: e.target.value || null })}>
                      <option value="">Sin vincular</option>
                      {participants.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {metrics && (
            <div style={cardSt}>
              <div style={{ ...labelSt, marginBottom: 10 }}>5. Vista previa</div>
              <MetricsView metrics={metrics} participants={participants} />
              <div style={{ textAlign: 'right', marginTop: 12 }}>
                <button className="action-btn btn-primary" disabled={busy || !stageOrder.length} onClick={save}>
                  {busy ? '⏳' : saved ? '💾 Reemplazar métricas' : '💾 Guardar métricas'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const COL_LABEL = Object.fromEntries(CRM_COLUMNS.map(c => [c.key, t(c.label)]));

function BucketRow({ label, b, est }) {
  return (
    <tr>
      <td style={{ ...tdSt, fontWeight: 600 }}>{label}</td>
      <td style={tdSt}>{fmtInt(b.opportunities)}</td>
      <td style={tdSt}>{fmtInt(b.open)}</td>
      <td style={tdSt}>{fmtInt(b.won)}</td>
      <td style={tdSt}>{fmtInt(b.lost)}</td>
      <td style={tdSt}>{fmtPct(b.winRate)}</td>
      <td style={tdSt}>{fmtNum(b.avgTicket)}</td>
      <td style={tdSt}>{fmtNum(b.avgCycleDays, 1)}</td>
      <td style={tdSt}>{fmtInt(b.stalled.count)} ({fmtPct(b.stalled.rate)}){est.stalled ? ' *' : ''}</td>
      <td style={tdSt}>{b.qualificationComplete === null ? '—' : fmtPct(b.qualificationComplete)}</td>
    </tr>
  );
}

function SegmentTable({ rows, first }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tableSt}>
        <thead><tr>{[first, 'Oport.', 'Ganadas', 'Perdidas', 'Win rate', 'Ticket prom.', 'Ciclo prom. (días)'].map(h => <th key={h} style={thSt}>{h}</th>)}</tr></thead>
        <tbody>{rows.map(([label, b]) => (
          <tr key={label}>
            <td style={{ ...tdSt, fontWeight: 600 }}>{label}</td><td style={tdSt}>{fmtInt(b.opportunities)}</td><td style={tdSt}>{fmtInt(b.won)}</td>
            <td style={tdSt}>{fmtInt(b.lost)}</td><td style={tdSt}>{fmtPct(b.winRate)}</td><td style={tdSt}>{fmtNum(b.avgTicket)}</td><td style={tdSt}>{fmtNum(b.avgCycleDays, 1)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

const HEAD = ['', 'Oport.', 'Abiertas', 'Ganadas', 'Perdidas', 'Win rate', 'Ticket prom.', 'Ciclo prom. (días)', 'Estancadas', 'Calificación completa'];

export function MetricsView({ metrics: m, participants }) {
  const nameOf = (id) => participants.find(p => p.id === id)?.name || 'Participante';
  const est = m.estimated || {};
  const people = Object.entries(m.byPerson || {});
  const conv = [['Total', m.total], ...people.map(([id, b]) => [nameOf(id), b])];
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={tableSt}>
          <thead><tr>{HEAD.map(h => <th key={h} style={thSt}>{h}</th>)}</tr></thead>
          <tbody>
            <BucketRow label="Total" b={m.total} est={est} />
            {people.map(([id, b]) => <BucketRow key={id} label={nameOf(id)} b={b} est={est} />)}
            {m.unlinked && <BucketRow label="Sin vincular" b={m.unlinked} est={est} />}
          </tbody>
        </table>
      </div>

      {m.stageOrder?.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 14 }}>
          <div style={{ ...labelSt }}>Conversión por etapa{est.stageConversion ? ' (estimada)' : ''}</div>
          <table style={tableSt}>
            <thead><tr><th style={thSt}>Etapa</th>{conv.map(([n]) => <th key={n} style={thSt}>{n}</th>)}</tr></thead>
            <tbody>
              {m.total.stageConversion.map((c, i) => (
                <tr key={i}>
                  <td style={tdSt}>{c.from} → {c.to === 'won' ? 'Ganada' : c.to}</td>
                  {conv.map(([n, b]) => <td key={n} style={tdSt}>{fmtPct(b.stageConversion[i]?.rate)} <span style={mutedSt}>({fmtInt(b.stageConversion[i]?.base)})</span></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <div style={labelSt}>Por canal</div>
        {m.byChannel ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableSt}>
              <thead><tr>{HEAD.map(h => <th key={h} style={thSt}>{h}</th>)}</tr></thead>
              <tbody>{Object.entries(m.byChannel).map(([ch, b]) => <BucketRow key={ch} label={ch} b={b} est={est} />)}</tbody>
            </table>
          </div>
        ) : <div style={mutedSt}>No disponible: el export no tiene la columna de origen.</div>}
      </div>

      {['byIndustry', 'bySize'].map(k => (
        <div key={k} style={{ marginTop: 14 }}>
          <div style={labelSt}>{k === 'byIndustry' ? 'Por industria del cliente' : 'Por tamaño del cliente'}</div>
          {m[k] ? <SegmentTable rows={Object.entries(m[k]).map(([label, b]) => [label, b])} first={k === 'byIndustry' ? 'Industria' : 'Tamaño'} />
            : <div style={mutedSt}>Falta: el export no tiene la columna de {k === 'byIndustry' ? 'industria' : 'tamaño del cliente'}. El ICP de System Design dirá "Falta".</div>}
        </div>
      ))}
      {m.bySegment?.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={labelSt}>Por industria y tamaño</div>
          <SegmentTable rows={m.bySegment.map(x => [`${x.industry} · ${x.size}`, x])} first="Segmento" />
        </div>
      )}

      {m.topOpen?.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={labelSt}>Las 3 oportunidades abiertas más grandes</div>
          <div style={{ ...mutedSt, fontSize: 11, marginBottom: 4 }}>Es lo único del export que se guarda fila por fila (empresa, monto, etapa, antigüedad y vendedor), solo para el módulo Cuentas. Se borra con el diagnóstico.</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableSt}>
              <thead><tr>{['Empresa', 'Monto', 'Etapa', 'Antigüedad (días)', 'Vendedor'].map(h => <th key={h} style={thSt}>{h}</th>)}</tr></thead>
              <tbody>{m.topOpen.map((o, i) => (
                <tr key={i}>
                  <td style={tdSt}>{o.company || 'Falta (mapea la columna Empresa)'}</td><td style={tdSt}>{fmtNum(o.amount)}</td>
                  <td style={tdSt}>{o.stage || '—'}</td><td style={tdSt}>{fmtInt(o.ageDays)}</td>
                  <td style={tdSt}>{o.sellerId ? nameOf(o.sellerId) : 'Sin vincular'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {m.lossReasons?.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={labelSt}>Motivos de pérdida</div>
          <div style={{ fontSize: 12 }}>{m.lossReasons.map(r => `${r.reason} (${r.count})`).join(' · ')}</div>
        </div>
      )}

      <div style={{ ...mutedSt, marginTop: 12, fontSize: 11 }}>
        {est.stalled && <div>* Estancadas: estimada desde la fecha de creación (falta la fecha de última modificación).</div>}
        {est.stageConversion && <div>Conversión por etapa estimada: hay oportunidades perdidas sin la etapa en la que se perdieron.</div>}
        {m.missingOptional?.length > 0 && <div>Columnas opcionales que faltan: {m.missingOptional.map(k => COL_LABEL[k] || k).join(', ')}.</div>}
        <div>Ticket y ciclo: solo oportunidades ganadas. Calificación completa: % de abiertas con todos los campos de calificación.</div>
      </div>
    </div>
  );
}
